import React from 'react'
import {Button} from '@thryvlabs/maverick'
import {toast} from 'react-toastify'
import {Controller} from 'react-hook-form'
import {useAsync} from '../utils/use-async'
import {useAuthClient} from '../utils/use-auth-client'
import '../styles/formScreensStyles.css'
import '../styles/accountDetails.css'
import {useSelector} from 'react-redux'
import {AddressSearchInput} from './address-search-input'
import {isLoqateCountry} from '../utils/address-provider-countries'
import {isInternationalTaxIdCountry} from '../utils/country-registry'
import {resolveStateOption} from '../utils/loqate-address'

const FONT = "Inter, 'Helvetica Neue', Arial, sans-serif"

const inputStyle = {
  width: '100%',
  height: '36px',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  background: '#ffffff',
  color: '#6a7181',
  fontFamily: FONT,
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '100%',
  paddingLeft: '12px',
  paddingRight: '12px',
  boxSizing: 'border-box',
  marginTop: 0,
  outline: 'none',
}

const errorStyle = {
  fontFamily: FONT,
  fontSize: '11px',
  color: '#d92d20',
  margin: '4px 0 0 0',
}

const fieldWrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  marginBottom: '16px',
}

// Small outline glyphs for the label icons (same role as the icons on the OOE
// New Account form). Decorative, hence aria-hidden on the wrapper.
const iconProps = {
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.4,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}
const ICONS = {
  email: (
    <svg {...iconProps}>
      <rect x="1.5" y="3" width="13" height="10" rx="1.5" />
      <path d="M2 4.5l6 4.5 6-4.5" />
    </svg>
  ),
  user: (
    <svg {...iconProps}>
      <circle cx="8" cy="5.5" r="2.7" />
      <path d="M2.5 14c.5-3 2.8-4.5 5.5-4.5s5 1.5 5.5 4.5" />
    </svg>
  ),
  phone: (
    <svg {...iconProps}>
      <rect x="4" y="1.5" width="8" height="13" rx="1.6" />
      <path d="M7 12.5h2" />
    </svg>
  ),
  building: (
    <svg {...iconProps}>
      <rect x="3" y="1.8" width="10" height="12.4" rx="1" />
      <path d="M6 5h1M9 5h1M6 8h1M9 8h1M7 14.2v-2.5h2v2.5" />
    </svg>
  ),
  globe: (
    <svg {...iconProps}>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M1.8 8h12.4M8 1.8c2 2 2 10.4 0 12.4M8 1.8c-2 2-2 10.4 0 12.4" />
    </svg>
  ),
  pin: (
    <svg {...iconProps}>
      <path d="M8 14.5s4.7-4.2 4.7-8a4.7 4.7 0 00-9.4 0c0 3.8 4.700 8 4.700 8z" />
      <circle cx="8" cy="6.5" r="1.7" />
    </svg>
  ),
}

const FieldLabel = ({icon, required, children}) => (
  <label className="payment-variant-label ad-label">
    {icon && (
      <span className="ad-label-icon" aria-hidden="true">
        {ICONS[icon]}
      </span>
    )}
    <span>
      {children}
      {required && <span className="ad-label-req"> *</span>}
    </span>
  </label>
)

export const AccountDetails = ({
  register,
  errors,
  listOfStates,
  validatedEmail,
  setValidatedEmail,
  email,
  emailRegistered,
  setEmailRegistered,
  setAccountInformation,
  setFormFilledStatus,
  trigger,
  control,
  state,
  handleSubmit,
  stateLabel = 'State',
  cityLabel = 'City',
  postalLabel = 'Postal Code',
  selectedCountry = 'US',
  onSessionResume,
  onCountryChange,
  supportedCountries = [],
  setValue,
}) => {
  const isAu = selectedCountry === 'AU'
  const isAusNZ = ['AU', 'NZ'].includes(selectedCountry)
  const {run} = useAsync({
    status: 'pending',
  })
  const client = useAuthClient()
  const {isMobileViewEnable} = useSelector(state => state.screenView)
  console.log({isMobileViewEnable})

  const disabledInputStyle = {
    ...inputStyle,
    cursor: emailRegistered ? 'not-allowed' : 'auto',
  }

  const disabledWrapStyle = {
    ...fieldWrapStyle,
    opacity: emailRegistered ? 0.4 : 1,
    cursor: emailRegistered ? 'not-allowed' : 'auto',
  }

  const eb = hasError => (hasError ? {border: '1px solid #d92d20'} : {})

  // Fill the rest of the address from a Loqate-retrieved selection. State is only
  // set when it maps confidently onto the form's list; otherwise the customer picks it.
  const handleAddressSelect = resolved => {
    const opts = {shouldValidate: true, shouldDirty: true}
    if (resolved.city) setValue('city', resolved.city, opts)
    if (resolved.postalCode) setValue('postCode', resolved.postalCode, opts)
    const stateOption = resolveStateOption(
      selectedCountry,
      resolved.state,
      listOfStates,
    )
    if (stateOption) setValue('state', stateOption, opts)
  }

  const preventAlphaInput = e => {
    if (
      e.key.length === 1 &&
      /[a-zA-Z]/.test(e.key) &&
      !e.ctrlKey &&
      !e.metaKey
    ) {
      e.preventDefault()
    }
  }

  const preventUnvalidatedEmail = () => {
    if (validatedEmail !== email && !emailRegistered) {
      setEmailRegistered(true)
      setValidatedEmail('')
    }
  }
  const handleEmailValidation = async () => {
    const emailRegex = /^[a-zA-Z0-9._+-]+@[^\s@]+\.[^\s@]+$/
    if (emailRegex.test(email)) {
      const isThryvEmail = email.includes('@thryv.com')
      if (isThryvEmail === false) {
        try {
          console.log('not a thryv acc')
          const response = await run(client(`orders/validate-email/${email}`))
          console.log({response})
          if (response?.orderCanContinue && !response?.thryvId) {
            setValidatedEmail(email)
            setEmailRegistered(false)
            // Disable the Next button when orderCanContinue is true
            //setAccountInformation(prev => ({...prev, email}))
          } else if (
            response?.accountCanUpgrade &&
            response?.thryvId &&
            response?.freeAccount
          ) {
            // Account exists but has no active subscription — allow checkout.
            // freeAccount: false (active paid sub) falls through to the error toast.
            setValidatedEmail(email)
            setEmailRegistered(false)
            onSessionResume?.(response.thryvId)
          } else {
            console.log('toast nees to eb triggered')
            toast.error(
              'Oops! Looks like we already have this email address registered. Please reach out to your Partner representative to confirm enrollment/purchase.',
            )
          }
        } catch (err) {
          // if (err.message === 'Recurly account not found') {
          toast.error(
            'Oops! Looks like we already have this email address registered. Please reach out to your Partner representative to confirm enrollment/purchase.',
          )
          // } else {
          //   toast.error(err.message)
          // }
        }
      } else {
        toast.error('Thryv addresses are not allowed')
      }
    } else {
      toast.error('Valid email address required')
    }
  }
  return (
    <div className="ad-form">
      <div className="section-heading">
        <div className="section-heading-title-container">
          <p className="section-heading-title">
            Payment Information{' '}
            <span className="required-field" style={{color: 'red'}}>
              *
            </span>
          </p>
          <p className="section-heading-subtitle">
            Provide your contact information to continue
          </p>
        </div>
      </div>

      {/* Email + Next button */}
      <div
        style={{
          marginTop: '16px',
          marginBottom: '4px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
          }}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <FieldLabel icon="email" required>
              Email Address
            </FieldLabel>
            <input
              type="text"
              placeholder="Email Address"
              style={{
                ...inputStyle,
                opacity: 1,
                border: errors.email ? '1px solid #d92d20' : inputStyle.border,
              }}
              {...register('email')}
              onChange={e => {
                register('email').onChange(e)
                preventUnvalidatedEmail()
              }}
            />
          </div>
          <Button
            variant="primary"
            type="button"
            name="Validate"
            onClick={handleEmailValidation}
            className="btn-validate"
            disabled={!emailRegistered}
          >
            Next
          </Button>
        </div>
        <p
          style={{
            ...errorStyle,
            visibility: errors.email ? 'visible' : 'hidden',
            marginTop: '4px',
            marginBottom: '16px',
          }}
        >
          {errors.email?.message || ' '}
        </p>
      </div>

      {/* First Name / Last Name */}
      <div
        className={`d-flex ${isMobileViewEnable ? 'flex-column' : ''}`}
        style={{
          gap: '8px',
          marginBottom: '16px',
          opacity: emailRegistered ? 0.4 : 1,
          cursor: emailRegistered ? 'not-allowed' : 'auto',
        }}
      >
        <div style={{...fieldWrapStyle, flex: 1, marginBottom: 0}}>
          <FieldLabel icon="user" required>
            First Name
          </FieldLabel>
          <input
            type="text"
            placeholder="Enter First Name..."
            style={{...disabledInputStyle, ...eb(errors.firstName)}}
            {...register('firstName')}
            disabled={emailRegistered}
          />
          <p
            style={{
              ...errorStyle,
              visibility: errors.firstName ? 'visible' : 'hidden',
            }}
          >
            {errors.firstName?.message || ' '}
          </p>
        </div>
        <div
          style={{
            ...fieldWrapStyle,
            flex: 1,
            marginBottom: 0,
            marginTop: isMobileViewEnable ? '20px' : 0,
          }}
        >
          <FieldLabel icon="user" required>
            Last Name
          </FieldLabel>
          <input
            type="text"
            placeholder="Enter Last Name..."
            style={{...disabledInputStyle, ...eb(errors.lastName)}}
            {...register('lastName')}
            disabled={emailRegistered}
          />
          <p
            style={{
              ...errorStyle,
              visibility: errors.lastName ? 'visible' : 'hidden',
            }}
          >
            {errors.lastName?.message || ' '}
          </p>
        </div>
      </div>

      {/* Mobile Number */}
      <div style={disabledWrapStyle}>
        <FieldLabel icon="phone" required>
          Mobile Number
        </FieldLabel>
        <input
          type="text"
          placeholder="Enter Mobile Number..."
          style={{...disabledInputStyle, ...eb(errors.mobile)}}
          maxLength={18}
          {...register('mobile')}
          disabled={emailRegistered}
          onKeyDown={preventAlphaInput}
        />
        <p
          style={{
            ...errorStyle,
            visibility: errors.mobile ? 'visible' : 'hidden',
          }}
        >
          {errors.mobile?.message || ' '}
        </p>
      </div>

      {/* Business Address section */}
      <div
        className="section-heading"
        style={{opacity: emailRegistered ? 0.4 : 1}}
      >
        <div className="section-heading-title-container">
          <p className="section-heading-title">
            Business Address{' '}
            <span className="required-field" style={{color: 'red'}}>
              *
            </span>
          </p>
          <p className="section-heading-subtitle">
            Provide the business address for this account
          </p>
        </div>
      </div>

      {/* Country Selector */}
      {onCountryChange && supportedCountries.length > 0 && (
        <div style={disabledWrapStyle}>
          <FieldLabel icon="globe">Country</FieldLabel>
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <select
              value={selectedCountry}
              onChange={e => onCountryChange(e.target.value)}
              disabled={emailRegistered}
              style={{
                ...disabledInputStyle,
                paddingRight: '28px',
                appearance: 'none',
                WebkitAppearance: 'none',
              }}
            >
              {supportedCountries.map(c => (
                <option key={c.isoValue} value={c.isoValue}>
                  {c.name}
                </option>
              ))}
            </select>
            <svg
              width="16"
              height="16"
              viewBox="0 0 12 12"
              fill="none"
              style={{
                position: 'absolute',
                right: '8px',
                pointerEvents: 'none',
              }}
            >
              <path
                d="M6.18568 8.49999C6.10703 8.50047 6.02907 8.48471 5.95626 8.45362C5.88345 8.42253 5.81722 8.37673 5.76138 8.31883L2.17575 4.57076C2.06322 4.45313 2 4.29359 2 4.12724C2 3.96088 2.06322 3.80134 2.17575 3.68371C2.28828 3.56608 2.44091 3.5 2.60005 3.5C2.75919 3.5 2.91182 3.56608 3.02435 3.68371L6.18568 6.99451L9.34701 3.68996C9.46133 3.58762 9.60839 3.53414 9.75879 3.54022C9.9092 3.54629 10.0519 3.61146 10.1583 3.72272C10.2647 3.83397 10.3271 3.9831 10.3329 4.14032C10.3387 4.29754 10.2875 4.45126 10.1896 4.57076L6.604 8.31883C6.49269 8.43424 6.34246 8.4993 6.18568 8.49999Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
      )}

      {/* Company */}
      <div style={disabledWrapStyle}>
        <FieldLabel icon="building" required>
          Company
        </FieldLabel>
        <input
          type="text"
          placeholder="Enter Company..."
          style={{...disabledInputStyle, ...eb(errors.company)}}
          {...register('company')}
          disabled={emailRegistered}
        />
        <p
          style={{
            ...errorStyle,
            visibility: errors.company ? 'visible' : 'hidden',
          }}
        >
          {errors.company?.message || ' '}
        </p>
      </div>

      {/* Business Phone / Website */}
      <div
        className={`d-flex ${isMobileViewEnable ? 'flex-column' : ''}`}
        style={{
          gap: '8px',
          marginBottom: '16px',
          opacity: emailRegistered ? 0.4 : 1,
          cursor: emailRegistered ? 'not-allowed' : 'auto',
        }}
      >
        <div style={{...fieldWrapStyle, flex: 1, marginBottom: 0}}>
          <FieldLabel icon="phone" required>
            Business Phone
          </FieldLabel>
          <input
            type="text"
            placeholder="Enter Business Phone..."
            style={{...disabledInputStyle, ...eb(errors.companyPhone)}}
            maxLength={18}
            {...register('companyPhone')}
            disabled={emailRegistered}
            onKeyDown={preventAlphaInput}
          />
          <p
            style={{
              ...errorStyle,
              visibility: errors.companyPhone ? 'visible' : 'hidden',
            }}
          >
            {errors.companyPhone?.message || ' '}
          </p>
        </div>
        <div
          style={{
            ...fieldWrapStyle,
            flex: 1,
            marginBottom: 0,
            marginTop: isMobileViewEnable ? '20px' : 0,
          }}
        >
          <FieldLabel icon="globe" required>
            Website
          </FieldLabel>
          <input
            type="text"
            placeholder="Enter Website..."
            style={{...disabledInputStyle, ...eb(errors.websiteName)}}
            {...register('websiteName')}
            disabled={emailRegistered}
          />
          <p
            style={{
              ...errorStyle,
              visibility: errors.websiteName ? 'visible' : 'hidden',
            }}
          >
            {errors.websiteName?.message || ' '}
          </p>
        </div>
      </div>

      {/* Customer Address */}
      <div style={disabledWrapStyle}>
        <FieldLabel icon="pin" required>
          Customer Address
        </FieldLabel>
        {isLoqateCountry(selectedCountry) && setValue ? (
          <Controller
            control={control}
            name="address"
            render={({field: {value, onChange, onBlur}}) => (
              <AddressSearchInput
                value={value || ''}
                onChange={onChange}
                onBlur={onBlur}
                onSelect={handleAddressSelect}
                country={selectedCountry}
                disabled={emailRegistered}
                placeholder="Search or enter Customer Address..."
                style={{...disabledInputStyle, ...eb(errors.address)}}
              />
            )}
          />
        ) : (
          <input
            type="text"
            placeholder="Enter Customer Address..."
            style={{...disabledInputStyle, ...eb(errors.address)}}
            {...register('address')}
            disabled={emailRegistered}
          />
        )}
        <p
          style={{
            ...errorStyle,
            visibility: errors.address ? 'visible' : 'hidden',
          }}
        >
          {errors.address?.message || ' '}
        </p>
      </div>

      {/* City / State / Postal Code */}
      <div
        className={`d-flex ${isMobileViewEnable ? 'flex-column' : ''}`}
        style={{
          gap: '8px',
          marginBottom: '16px',
          opacity: emailRegistered ? 0.4 : 1,
          cursor: emailRegistered ? 'not-allowed' : 'auto',
        }}
      >
        <div style={{...fieldWrapStyle, flex: 1, marginBottom: 0}}>
          <FieldLabel icon="pin" required>
            {cityLabel}
          </FieldLabel>
          <input
            type="text"
            placeholder={`Enter ${cityLabel}...`}
            style={{...disabledInputStyle, ...eb(errors.city)}}
            {...register('city')}
            disabled={emailRegistered}
          />
          <p
            style={{
              ...errorStyle,
              visibility: errors.city ? 'visible' : 'hidden',
            }}
          >
            {errors.city?.message || ' '}
          </p>
        </div>
        <div
          style={{
            ...fieldWrapStyle,
            flex: 1,
            marginBottom: 0,
            marginTop: isMobileViewEnable ? '20px' : 0,
          }}
        >
          <FieldLabel icon="pin" required>
            {stateLabel}
          </FieldLabel>
          <Controller
            control={control}
            name="state"
            render={({field: {onChange, name}}) => (
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <select
                  name={name}
                  style={{
                    ...disabledInputStyle,
                    ...eb(errors.state?.name),
                    paddingRight: '28px',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                  }}
                  disabled={emailRegistered}
                  value={state?.value || ''}
                  onChange={e =>
                    onChange(
                      listOfStates.find(s => s.value === e.target.value) ||
                        null,
                    )
                  }
                >
                  <option value="">Select {stateLabel}</option>
                  {listOfStates.map(({name: stateName, value}) => (
                    <option key={value} value={value}>
                      {stateName}
                    </option>
                  ))}
                </select>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 12 12"
                  fill="none"
                  style={{
                    position: 'absolute',
                    right: '8px',
                    pointerEvents: 'none',
                  }}
                >
                  <path
                    d="M6.18568 8.49999C6.10703 8.50047 6.02907 8.48471 5.95626 8.45362C5.88345 8.42253 5.81722 8.37673 5.76138 8.31883L2.17575 4.57076C2.06322 4.45313 2 4.29359 2 4.12724C2 3.96088 2.06322 3.80134 2.17575 3.68371C2.28828 3.56608 2.44091 3.5 2.60005 3.5C2.75919 3.5 2.91182 3.56608 3.02435 3.68371L6.18568 6.99451L9.34701 3.68996C9.46133 3.58762 9.60839 3.53414 9.75879 3.54022C9.9092 3.54629 10.0519 3.61146 10.1583 3.72272C10.2647 3.83397 10.3271 3.9831 10.3329 4.14032C10.3387 4.29754 10.2875 4.45126 10.1896 4.57076L6.604 8.31883C6.49269 8.43424 6.34246 8.4993 6.18568 8.49999Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
            )}
          />
          <p
            style={{
              ...errorStyle,
              visibility: errors.state?.name ? 'visible' : 'hidden',
            }}
          >
            {errors.state?.name?.message || ' '}
          </p>
        </div>
        <div
          style={{
            ...fieldWrapStyle,
            flex: 1,
            marginBottom: 0,
            marginTop: isMobileViewEnable ? '20px' : 0,
          }}
        >
          <FieldLabel icon="pin" required>
            {postalLabel}
          </FieldLabel>
          <input
            type="text"
            placeholder={`Enter ${postalLabel}...`}
            style={{...disabledInputStyle, ...eb(errors.postCode)}}
            {...register('postCode')}
            disabled={emailRegistered}
          />
          <p
            style={{
              ...errorStyle,
              visibility: errors.postCode ? 'visible' : 'hidden',
            }}
          >
            {errors.postCode?.message || ' '}
          </p>
        </div>
      </div>

      {isInternationalTaxIdCountry(selectedCountry) && (
        <div style={disabledWrapStyle}>
          <FieldLabel icon="building">Tax ID / VAT / GST Number</FieldLabel>
          <input
            type="text"
            style={disabledInputStyle}
            {...register('abn')}
            disabled={emailRegistered}
          />
        </div>
      )}

      {isAusNZ && (
        <div style={disabledWrapStyle}>
          <FieldLabel icon="building">
            {isAu
              ? 'Australian Business Number (ABN)'
              : 'New Zealand Business Number (NZBN)'}
          </FieldLabel>
          <input
            type="text"
            style={disabledInputStyle}
            {...register('abn')}
            disabled={emailRegistered}
          />
          {errors.abn && <p style={errorStyle}>{errors.abn.message}</p>}
          <a
            href={
              isAu
                ? 'https://abr.business.gov.au/'
                : 'https://www.nzbn.govt.nz/'
            }
            target="_blank"
            rel="noreferrer"
            className="abn-link"
            style={{fontSize: '12px'}}
          >
            {isAu ? 'ABN' : 'NZBN'} Lookup
          </a>
        </div>
      )}
    </div>
  )
}
